'use client';

import { ReportModalAspectRadarPoint } from '@/domain/reporting/reporting-modal-aggregations';
import {
    REPORT_MODAL_ASPECT_RADAR_DIMENSIONS,
    buildReportModalAspectRadarChartData,
} from '@/domain/reporting/reporting-modal-charts';
import { trimLabel } from '@/domain/reporting/reporting-shared.formatters';
import { toRounded } from '@/domain/reporting/reporting-shared.math';
import { FC, useMemo } from 'react';

type ReportModalAspectRadarChartProps = {
    points: ReportModalAspectRadarPoint[];
};

const ReportModalAspectRadarChart: FC<ReportModalAspectRadarChartProps> = ({ points }) => {
    const chartData = useMemo(() => buildReportModalAspectRadarChartData(points), [points]);

    if (!chartData) {
        return (
            <div className="flex h-[290px] items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 text-sm text-slate-600">
                Нет данных по аспектам для построения веб-графа.
            </div>
        );
    }

    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
            <div className="flex justify-center">
                <svg
                    className="h-[260px] w-full"
                    viewBox={`0 0 ${chartData.width} ${chartData.height}`}
                    role="img"
                    aria-label="Веб-граф аспектов"
                >
                    {chartData.levels.map((level, index) => (
                        <polygon
                            key={`radar-level-${index}`}
                            points={level.polygon}
                            fill="none"
                            stroke="#d9e2ec"
                            strokeWidth="1"
                        />
                    ))}
                    {chartData.axis.map(({ axisEnd, point, angle }) => (
                        <g key={`radar-axis-${point.key}`}>
                            <line
                                x1={chartData.centerX}
                                y1={chartData.centerY}
                                x2={axisEnd.x}
                                y2={axisEnd.y}
                                stroke="#cbd5e1"
                                strokeWidth="1"
                            />
                            <text
                                x={toRounded(
                                    chartData.centerX +
                                        Math.cos(angle) * (REPORT_MODAL_ASPECT_RADAR_DIMENSIONS.radius + 14),
                                )}
                                y={toRounded(
                                    chartData.centerY +
                                        Math.sin(angle) * (REPORT_MODAL_ASPECT_RADAR_DIMENSIONS.radius + 14),
                                )}
                                fontSize="10"
                                fill="#334155"
                                textAnchor={Math.cos(angle) > 0.2 ? 'start' : Math.cos(angle) < -0.2 ? 'end' : 'middle'}
                                dominantBaseline="middle"
                            >
                                {trimLabel(point.title, 22)}
                                <title>{point.title}</title>
                            </text>
                        </g>
                    ))}
                    <polygon
                        points={chartData.positivePolygon}
                        fill="rgba(16, 185, 129, 0.18)"
                        stroke="#10b981"
                        strokeWidth="2"
                    />
                    <polygon
                        points={chartData.negativePolygon}
                        fill="rgba(244, 63, 94, 0.18)"
                        stroke="#f43f5e"
                        strokeWidth="2"
                    />
                    {chartData.axis.map(({ point, positiveMarker, negativeMarker }) => (
                        <g key={`radar-marker-${point.key}`}>
                            <circle
                                cx={toRounded(positiveMarker.x)}
                                cy={toRounded(positiveMarker.y)}
                                r="2.8"
                                fill="#10b981"
                                stroke="#ffffff"
                                strokeWidth="1"
                            />
                            <circle
                                cx={toRounded(negativeMarker.x)}
                                cy={toRounded(negativeMarker.y)}
                                r="2.8"
                                fill="#f43f5e"
                                stroke="#ffffff"
                                strokeWidth="1"
                            />
                        </g>
                    ))}
                </svg>
            </div>
            <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-slate-600">
                <span className="inline-flex items-center gap-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                    Позитивный сентимент
                </span>
                <span className="inline-flex items-center gap-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                    Негативный сентимент
                </span>
            </div>
        </div>
    );
};

export default ReportModalAspectRadarChart;
